import { create } from "zustand";
import type { GameState, PlayerId, PlacedTile, Tile, PremiumSquare, GameMode, Complexity, WordGrid, SolvedWord } from "../../shared/types";

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function distributeRack(tiles: Tile[]): [Tile[], Tile[], Tile[], Tile[]] {
  const shuffled = shuffle(tiles);
  const sides: [Tile[], Tile[], Tile[], Tile[]] = [[], [], [], []];
  shuffled.forEach((tile, i) => sides[i % 4].push(tile));
  return sides;
}

function flattenRack(sides: [Tile[], Tile[], Tile[], Tile[]]): Tile[] {
  return [...sides[0], ...sides[1], ...sides[2], ...sides[3]];
}

interface GameStore {
  roomId: string;
  mode: GameMode;
  phase: GameState["phase"];
  myBoard: PlacedTile[];
  opponentBoard: PlacedTile[];
  premiumSquares: PremiumSquare[];
  myRack: Tile[];
  opponentRack: Tile[];
  rackSides: [Tile[], Tile[], Tile[], Tile[]];
  myScore: number;
  opponentScore: number;
  myPenalties: number;
  opponentPenalties: number;
  timeLimitMs: number;
  complexity: Complexity;
  winner: PlayerId | null;
  currentTurn: PlayerId;
  eventLog: string[];
  startedAt: number;
  puzzle: WordGrid | null;
  solvedWords: Record<string, SolvedWord[]>;
  firstCompletedBy?: PlayerId;
  firstCompletedAt?: number;
  gracePeriodEnd?: number;
  penaltyFlash: boolean;

  setGame: (state: Partial<GameStore>) => void;
  syncRackFromServer: (serverRack: Tile[]) => void;
  moveTileWithinSide: (sideIndex: number, fromIndex: number, toIndex: number) => void;
  moveTileBetweenSides: (fromSide: number, fromIndex: number, toSide: number, toIndex: number) => void;
  placeTile: (player: PlayerId, tile: PlacedTile) => void;
  recallTile: (player: PlayerId, tileId: string) => void;
  setPenaltyFlash: (flash: boolean) => void;
  reset: () => void;
}

const initial: Omit<
  GameStore,
  "setGame" | "syncRackFromServer" | "moveTileWithinSide" | "moveTileBetweenSides" | "placeTile" | "recallTile" | "setPenaltyFlash" | "reset"
> = {
  roomId: "",
  mode: "arcade",
  phase: "lobby",
  myBoard: [],
  opponentBoard: [],
  premiumSquares: [],
  myRack: [],
  opponentRack: [],
  rackSides: [[], [], [], []],
  myScore: 0,
  opponentScore: 0,
  myPenalties: 0,
  opponentPenalties: 0,
  timeLimitMs: 180000,
  complexity: "low",
  winner: null,
  currentTurn: "p1",
  eventLog: [],
  startedAt: 0,
  puzzle: null,
  solvedWords: {},
  penaltyFlash: false,
};

export const useGameStore = create<GameStore>((set, get) => ({
  ...initial,
  setGame: (state) => {
    const current = get();
    const newRack = state.myRack;
    // If rack changed from server, reconcile with current sides
    if (newRack && newRack !== current.myRack) {
      const currentTotal = flattenRack(current.rackSides);
      const currentIds = new Set(currentTotal.map((t) => t.id));
      const newIds = new Set(newRack.map((t) => t.id));

      // Tiles that were placed (in current but not in new)
      const placedIds = new Set([...currentIds].filter((id) => !newIds.has(id)));
      // New tiles drawn (in new but not in current)
      const drawnTiles = newRack.filter((t) => !currentIds.has(t.id));

      // Build new sides: remove placed, keep remaining, add drawn to random sides
      let newSides: [Tile[], Tile[], Tile[], Tile[]] = [
        current.rackSides[0].filter((t) => !placedIds.has(t.id)),
        current.rackSides[1].filter((t) => !placedIds.has(t.id)),
        current.rackSides[2].filter((t) => !placedIds.has(t.id)),
        current.rackSides[3].filter((t) => !placedIds.has(t.id)),
      ];

      // Distribute drawn tiles randomly across sides
      const shuffledDrawn = shuffle(drawnTiles);
      shuffledDrawn.forEach((tile) => {
        // Find the side with fewest tiles
        const minSide = newSides.reduce((min, side, idx) =>
          side.length < newSides[min].length ? idx : min
        , 0);
        newSides[minSide].push(tile);
      });

      state.rackSides = newSides;
    }
    set(state);
  },
  syncRackFromServer: (serverRack) => {
    const current = get();
    const currentTotal = flattenRack(current.rackSides);
    const currentIds = new Set(currentTotal.map((t) => t.id));
    const newIds = new Set(serverRack.map((t) => t.id));

    const placedIds = new Set([...currentIds].filter((id) => !newIds.has(id)));
    const drawnTiles = serverRack.filter((t) => !currentIds.has(t.id));

    let newSides: [Tile[], Tile[], Tile[], Tile[]] = [
      current.rackSides[0].filter((t) => !placedIds.has(t.id)),
      current.rackSides[1].filter((t) => !placedIds.has(t.id)),
      current.rackSides[2].filter((t) => !placedIds.has(t.id)),
      current.rackSides[3].filter((t) => !placedIds.has(t.id)),
    ];

    const shuffledDrawn = shuffle(drawnTiles);
    shuffledDrawn.forEach((tile) => {
      const minSide = newSides.reduce((min, side, idx) =>
        side.length < newSides[min].length ? idx : min
      , 0);
      newSides[minSide].push(tile);
    });

    set({ myRack: serverRack, rackSides: newSides });
  },
  moveTileWithinSide: (sideIndex, fromIndex, toIndex) =>
    set((s) => {
      const sides = [...s.rackSides] as [Tile[], Tile[], Tile[], Tile[]];
      const side = [...sides[sideIndex]];
      const [removed] = side.splice(fromIndex, 1);
      side.splice(toIndex, 0, removed);
      sides[sideIndex] = side;
      return { rackSides: sides, myRack: flattenRack(sides) };
    }),
  moveTileBetweenSides: (fromSide, fromIndex, toSide, toIndex) =>
    set((s) => {
      const sides = [...s.rackSides] as [Tile[], Tile[], Tile[], Tile[]];
      const from = [...sides[fromSide]];
      const to = [...sides[toSide]];
      const [removed] = from.splice(fromIndex, 1);
      to.splice(toIndex, 0, removed);
      sides[fromSide] = from;
      sides[toSide] = to;
      return { rackSides: sides, myRack: flattenRack(sides) };
    }),
  placeTile: (_player, tile) =>
    set((s) => ({
      myBoard: [...s.myBoard, tile],
    })),
  recallTile: (_player, tileId) =>
    set((s) => ({
      myBoard: s.myBoard.filter((t) => t.tileId !== tileId),
    })),
  setPenaltyFlash: (flash) => set({ penaltyFlash: flash }),
  reset: () => set({ ...initial }),
}));
